import { apiClient } from '@/api/client';
import { computeFormDataHash } from '@/utils/data/formDataHash';
import type {
  ExportResponse,
  TransactionResponse,
  TransactionWithArtifactsResponse,
  CuratedArtifactResponse,
  DeltaCheckErrorsRequest,
  ErrorReport,
  ErrorReviewResponse,
  ValidationFormData,
  ValidationMetadata,
  WebhookPayload,
} from '@/api/types';

export async function getTransactionFull(
  transactionId: string
): Promise<TransactionWithArtifactsResponse> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}`, {
    params: { include: 'artifacts' },
  });
  return data;
}

export async function getExportPreview(transactionId: string): Promise<WebhookPayload> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}/export/preview`);
  return data;
}

export async function getTransactionPdf(transactionId: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}/pdf`, {
    responseType: 'blob',
  });
  return data;
}

export async function getPageImage(imageUrl: string): Promise<Blob> {
  const { data } = await apiClient.get(imageUrl, {
    responseType: 'blob',
  });
  return data;
}

export async function getDebugArtifact(
  transactionId: string,
  artifactName: string
): Promise<unknown> {
  const { data } = await apiClient.get(
    `/api/v1/transactions/${transactionId}/artifacts/${artifactName}`
  );
  return data;
}

export async function getTransactionArtifactText(
  transactionId: string,
  artifactName: string
): Promise<string> {
  const { data } = await apiClient.get(
    `/api/v1/transactions/${transactionId}/artifacts/${artifactName}`,
    { responseType: 'text', transformResponse: (v) => v }
  );
  return data as string;
}

export async function saveCuratedData(
  transactionId: string,
  curatedData: CuratedArtifactResponse
): Promise<TransactionResponse> {
  const { data } = await apiClient.put(`/api/v1/transactions/${transactionId}/curation`, {
    curatedData,
  });
  return data;
}

export async function exportDocument(
  transactionId: string,
  curatedData: CuratedArtifactResponse
): Promise<ExportResponse> {
  const { data } = await apiClient.post(`/api/v1/transactions/${transactionId}/export`, {
    curatedData,
  });
  return data;
}

export async function getErrorReview(transactionId: string): Promise<ErrorReviewResponse> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}/error-review`);
  return data;
}

export async function checkErrorsDelta(
  transactionId: string,
  request: DeltaCheckErrorsRequest,
  signal?: AbortSignal
): Promise<ErrorReport> {
  const { data } = await apiClient.post(
    `/api/v1/transactions/${transactionId}/error-checks`,
    request,
    { signal }
  );
  return data;
}

export async function fetchStoredErrorReport(
  transactionId: string,
  signal?: AbortSignal
): Promise<ErrorReport> {
  const { data } = await apiClient.get(
    `/api/v1/transactions/${transactionId}/error-checks/initial`,
    { signal }
  );
  return data;
}

export function buildFullDeltaRequest(formData: ValidationFormData): DeltaCheckErrorsRequest {
  return {
    changedCargo: formData.dangerousGoods,
    changedCtus: formData.cargoTransportUnits,
    cargoIds: formData.dangerousGoods.map((g) => g.cargoId).filter((id): id is string => !!id),
    ctuIds: formData.cargoTransportUnits.map((c) => c.ctuId).filter((id): id is string => !!id),
  };
}

export function buildSavePayload(
  formData: ValidationFormData,
  metadata: ValidationMetadata
): { curatedData: CuratedArtifactResponse; formHash: string } {
  return {
    curatedData: { form_data: formData, metadata },
    formHash: computeFormDataHash(formData),
  };
}
